import { useState } from "react";
import { Download } from "lucide-react";
import type { ImageArtifact } from "~/components/assistant/types";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from "~/components/ui/dialog";

interface ImageMessageProps {
  image: ImageArtifact;
  debugMode?: boolean;
}

// Below this threshold the inline download chip dwarfs the image, so we hide
// it and rely on the lightbox's labeled Download action instead.
const DOWNLOAD_CHIP_MIN_SIZE = 100;

export function ImageMessage({ image }: ImageMessageProps) {
  const altText = image.summary || image.filename || image.artifact_name || "Image artifact";
  const title = image.filename || image.artifact_name || "Image";
  const downloadName = image.filename ?? "";
  const [showDownloadChip, setShowDownloadChip] = useState(false);

  return (
    <Dialog>
      <div className="relative inline-block">
        <DialogTrigger asChild>
          <button
            type="button"
            className="block rounded-lg overflow-hidden cursor-zoom-in focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            aria-label={`Open ${title}`}
          >
            <img
              src={image.content.url}
              alt={altText}
              className="max-w-full max-h-96 object-contain"
              onLoad={(e) => {
                const img = e.currentTarget;
                setShowDownloadChip(
                  img.naturalWidth >= DOWNLOAD_CHIP_MIN_SIZE &&
                    img.naturalHeight >= DOWNLOAD_CHIP_MIN_SIZE
                );
              }}
            />
          </button>
        </DialogTrigger>

        {showDownloadChip && (
          <a
            href={image.content.url}
            download={downloadName}
            className="absolute bottom-2 right-2 flex items-center justify-center size-7 rounded-full bg-black/40 text-white hover:bg-black/60 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            aria-label="Download image"
          >
            <Download className="size-3.5" />
          </a>
        )}
      </div>

      <DialogContent
        className="flex flex-col gap-3 w-fit min-w-[320px] max-w-[95vw] sm:max-w-[95vw] max-h-[95vh]"
        closeButtonClassName="focus:ring-0 focus:ring-offset-0 focus-visible:bg-gray-200 active:bg-gray-300"
      >
        <DialogTitle className="pr-10 text-sm font-medium truncate">{title}</DialogTitle>
        <DialogDescription className="sr-only">
          {image.summary || `Full-size view of ${title}`}
        </DialogDescription>
        <div className="relative flex items-center justify-center rounded bg-black/5">
          <img
            src={image.content.url}
            alt={altText}
            className="max-w-full max-h-[80vh] object-contain"
          />
          {showDownloadChip && (
            <a
              href={image.content.url}
              download={downloadName}
              className="absolute bottom-2 right-2 flex items-center justify-center size-9 rounded-full bg-black/40 text-white hover:bg-black/60 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
              aria-label="Download image"
            >
              <Download className="size-4" />
            </a>
          )}
        </div>
        {image.summary && (
          <p className="text-sm text-gray-700 whitespace-pre-wrap">{image.summary}</p>
        )}
        {!showDownloadChip && (
          <div className="flex justify-end">
            <Button asChild variant="outline" size="sm" aria-label="Download image">
              <a href={image.content.url} download={downloadName}>
                <Download className="size-3.5" />
                Download
              </a>
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
